import { createSignal, createMemo, createEffect, For, Show } from 'solid-js';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { useBotFanOut } from '../hooks/useBotFanOut';
import { useActiveBot } from '../state/activeBot';
import BotStatusBadge from '../components/BotStatusBadge';
import DispatchResultPill from '../components/DispatchResultPill';
import Text from '../components/Text';
import { EmptyState } from '../components/EmptyState';
import { CHAT_MAX_LENGTH, type PluginTasks, type TaskDef } from './botengine/types';
import type { BotRegistryEntry } from '../components/BotSelector';
import { BOT_ACTIONS } from '../services/resourceActions';
import TaskParameterInput from '../components/TaskParameterInput';
import styles from './MultiOrchestrate.module.css';

export default function MultiOrchestrate() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { bots, group, targetIds } = useActiveBot();
    const fanOut = useBotFanOut();

    const [mode, setMode] = createSignal<'chat' | 'task'>('chat');

    const [chatText, setChatText] = createSignal('');
    const [chatBaseSec, setChatBaseSec] = createSignal(0);
    const [chatJitterSec, setChatJitterSec] = createSignal(0);

    const [selectedKey, setSelectedKey] = createSignal<string>('');
    const [taskArgs, setTaskArgs] = createSignal<Record<string, Record<string, string>>>({});

    const [availableByBot, setAvailableByBot] = createSignal<Record<string, PluginTasks[]>>({});
    const [loadingTasks, setLoadingTasks] = createSignal<Set<string>>(new Set());

    const targetBotIds = createMemo(() =>
        targetIds(mode() === 'chat' ? BOT_ACTIONS.WorldWrite : BOT_ACTIONS.TasksRun));
    const targetBots = createMemo<BotRegistryEntry[]>(() =>
        targetBotIds()
            .map(id => bots().find(b => b.id === id))
            .filter((b): b is BotRegistryEntry => !!b));

    createEffect(() => {
        if (mode() !== 'task') return;
        const ids = targetBotIds();
        const cache = availableByBot();
        const loading = loadingTasks();
        const toFetch = ids.filter(id => !(id in cache) && !loading.has(id));
        if (toFetch.length === 0) return;
        setLoadingTasks(prev => {
            const next = new Set(prev);
            for (const id of toFetch) next.add(id);
            return next;
        });
        for (const id of toFetch) {
            void (async () => {
                const res = await api.botTask.getAvailableTasks(id);
                const list: PluginTasks[] = isOk(res.data) ? ((res.data.value.available ?? []) as unknown as PluginTasks[]) : [];
                setAvailableByBot(prev => ({ ...prev, [id]: list }));
                setLoadingTasks(prev => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
            })();
        }
    });

    const intersection = createMemo<PluginTasks[]>(() => {
        const ids = targetBotIds();
        if (ids.length === 0) return [];
        const cache = availableByBot();
        const lists = ids.map(id => cache[id]);
        if (lists.some(l => !l)) return [];
        const keyCounts = new Map<string, { plugin: string; task: TaskDef; count: number }>();
        for (const list of lists) {
            const seen = new Set<string>();
            for (const pg of list) {
                for (const task of pg.tasks) {
                    const k = `${pg.plugin}:${task.name}`;
                    if (seen.has(k)) continue;
                    seen.add(k);
                    const e = keyCounts.get(k);
                    if (e) e.count++;
                    else keyCounts.set(k, { plugin: pg.plugin, task, count: 1 });
                }
            }
        }
        const grouped = new Map<string, TaskDef[]>();
        for (const { plugin, task, count } of keyCounts.values()) {
            if (count !== lists.length) continue;
            if (!grouped.has(plugin)) grouped.set(plugin, []);
            grouped.get(plugin)!.push(task);
        }
        return [...grouped.entries()].map(([plugin, tasks]) => ({ plugin, tasks }));
    });

    const tasksLoading = createMemo(() =>
        targetBotIds().some(id => loadingTasks().has(id))
    );

    const selectedTaskDef = createMemo<{ plugin: string; task: TaskDef } | null>(() => {
        const k = selectedKey();
        if (!k) return null;
        const [plugin, taskName] = k.split(':');
        const pg = intersection().find(p => p.plugin === plugin);
        const task = pg?.tasks.find(t => t.name === taskName);
        return task ? { plugin, task } : null;
    });

    const setArg = (key: string, param: string, value: string) => {
        setTaskArgs(prev => ({
            ...prev,
            [key]: { ...(prev[key] ?? {}), [param]: value },
        }));
    };

    const sendChat = async () => {
        const text = chatText();
        const ids = targetBotIds();
        if (!text || ids.length === 0) return;
        if (text.length > CHAT_MAX_LENGTH) {
            toast?.error(t('MULTI_CHAT_TOO_LONG', String(text.length), String(CHAT_MAX_LENGTH)));
            return;
        }
        await fanOut.run(ids,
            botId => api.botLifecycle.sendCommand(botId, { command: text }),
            { success: t('MULTI_RESULT_OK'), failure: t('MULTI_RESULT_ERROR') },
            { baseMs: Math.max(0, chatBaseSec()) * 1000, jitterMs: Math.max(0, chatJitterSec()) * 1000 });
    };

    const enqueueTask = async () => {
        const def = selectedTaskDef();
        const ids = targetBotIds();
        if (!def || ids.length === 0) return;
        const key = `${def.plugin}:${def.task.name}`;
        const args = taskArgs()[key] ?? {};

        for (const param of def.task.parameters) {
            if (param.required && !(args[param.name]?.length)) {
                toast?.error(t('MULTI_TASK_MISSING_ARG', param.name));
                return;
            }
        }

        await fanOut.run(ids,
            botId => api.botTask.enqueueTask(botId, { plugin: def.plugin, task: def.task.name, args: args as never }),
            { success: t('MULTI_RESULT_OK'), failure: t('MULTI_RESULT_ERROR') });
    };

    const targetCount = () => targetBotIds().length;
    const dispatching = fanOut.running;
    const chatTooLong = () => chatText().length > CHAT_MAX_LENGTH;
    const canSendChat = () => !dispatching() && targetCount() > 0 && chatText().length > 0 && !chatTooLong();
    const canEnqueueTask = () => !dispatching() && targetCount() > 0 && !!selectedTaskDef() && !tasksLoading();

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <h1><Text val="MULTI_PAGE_TITLE" /></h1>
                <span class={styles.entryCount}>
                    {t('MULTI_TARGET_COUNT', String(targetCount()))}
                </span>
            </div>

            <div class={styles.layout}>
                <section class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                    <div class={styles.pickerHeader}>
                        <h2 style={{ margin: 0, 'font-size': 'var(--font-size-lg)' }}>
                            {group()?.name ?? t('MULTI_TARGETS')}
                        </h2>
                    </div>
                    <span class={styles.fieldHint}><Text val="MULTI_TARGETS_HINT" /></span>

                    <Show when={targetBots().length > 0} fallback={
                        <EmptyState icon="fa-robot" title={t('MULTI_NO_TARGETS')} />
                    }>
                        <div class={styles.botList}>
                            <For each={targetBots()}>{(bot) => (
                                <div class={styles.botRow}>
                                    <img class={styles.avatar}
                                        src={`https://mc-heads.net/avatar/${bot.minecraftUuid}/24`} alt="" />
                                    <div class={styles.botInfo}>
                                        <span class={styles.botName}>{bot.minecraftUsername}</span>
                                        <span class={styles.botUuid}>{bot.minecraftUuid}</span>
                                    </div>
                                    <BotStatusBadge state={bot.state} />
                                    <Show when={fanOut.results()[bot.id]}>
                                        {(result) => <DispatchResultPill result={result()} />}
                                    </Show>
                                </div>
                            )}</For>
                        </div>
                    </Show>
                </section>

                <section class="fcard v" style={{ gap: 'var(--spacing-md)' }}>
                    <div class={styles.tabBar}>
                        <button type="button"
                            class={`${styles.tab} ${mode() === 'chat' ? styles.tabActive : ''}`}
                            onClick={() => setMode('chat')}>
                            <i class="fa-solid fa-message" /> <Text val="MULTI_TAB_CHAT" />
                        </button>
                        <button type="button"
                            class={`${styles.tab} ${mode() === 'task' ? styles.tabActive : ''}`}
                            onClick={() => setMode('task')}>
                            <i class="fa-solid fa-list-check" /> <Text val="MULTI_TAB_TASK" />
                        </button>
                    </div>

                    <Show when={mode() === 'chat'}>
                        <form class="v" style={{ gap: 'var(--spacing-md)' }}
                            onSubmit={(e) => { e.preventDefault(); void sendChat(); }}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MULTI_CHAT_LABEL" /></span>
                                <input type="text"
                                    placeholder={t('MULTI_CHAT_PLACEHOLDER') ?? ''}
                                    value={chatText()}
                                    maxLength={CHAT_MAX_LENGTH * 2}
                                    onInput={(e) => setChatText(e.currentTarget.value)} />
                                <span class={`${styles.charCount} ${chatTooLong() ? styles.charCountOver : ''}`}>
                                    {chatText().length} / {CHAT_MAX_LENGTH}
                                </span>
                            </label>

                            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <div class="h" style={{ gap: 'var(--spacing-md)' }}>
                                    <label class="v" style={{ gap: 'var(--spacing-xs)', flex: 1 }}>
                                        <span class={styles.fieldLabel}><Text val="MULTI_CHAT_DELAY_LABEL" /></span>
                                        <input type="number"
                                            min="0"
                                            step="0.1"
                                            value={chatBaseSec()}
                                            onInput={(e) => setChatBaseSec(Math.max(0, Number(e.currentTarget.value) || 0))} />
                                    </label>
                                    <label class="v" style={{ gap: 'var(--spacing-xs)', flex: 1 }}>
                                        <span class={styles.fieldLabel}><Text val="MULTI_CHAT_JITTER_LABEL" /></span>
                                        <input type="number"
                                            min="0"
                                            step="0.1"
                                            value={chatJitterSec()}
                                            onInput={(e) => setChatJitterSec(Math.max(0, Number(e.currentTarget.value) || 0))} />
                                    </label>
                                </div>
                                <span class={styles.fieldHint}><Text val="MULTI_CHAT_DELAY_HINT" /></span>
                            </div>

                            <div class="h" style={{ 'justify-content': 'flex-end' }}>
                                <button type="submit" class="btn btn-primary"
                                    disabled={!canSendChat()}>
                                    <Show when={dispatching()} fallback={<><i class="fa-solid fa-paper-plane" /> <Text val="MULTI_SEND" /></>}>
                                        <i class="fa-solid fa-spinner fa-spin" /> <Text val="MULTI_SENDING" />
                                    </Show>
                                </button>
                            </div>
                        </form>
                    </Show>

                    <Show when={mode() === 'task'}>
                        <form class="v" style={{ gap: 'var(--spacing-md)' }}
                            onSubmit={(e) => { e.preventDefault(); void enqueueTask(); }}>
                            <Show when={targetCount() > 0 && tasksLoading()}>
                                <div class={styles.taskHint}>
                                    <i class="fa-solid fa-spinner fa-spin" /> <Text val="MULTI_LOADING_TASKS" />
                                </div>
                            </Show>
                            <Show when={targetCount() > 0 && !tasksLoading() && intersection().length === 0}>
                                <div class={styles.taskHint}><Text val="MULTI_TASK_NO_INTERSECTION" /></div>
                            </Show>

                            <Show when={targetCount() > 0 && !tasksLoading() && intersection().length > 0}>
                                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span class={styles.fieldLabel}><Text val="MULTI_TASK_PICK" /></span>
                                    <select value={selectedKey()}
                                        onChange={(e) => { setSelectedKey(e.currentTarget.value); }}>
                                        <option value="">{t('MULTI_TASK_PICK_PLACEHOLDER') ?? '-- pick a task --'}</option>
                                        <For each={intersection()}>{(pg) =>
                                            <For each={pg.tasks}>{(task) =>
                                                <option value={`${pg.plugin}:${task.name}`}>{pg.plugin}: {task.name}</option>
                                            }</For>
                                        }</For>
                                    </select>
                                </label>

                                <Show when={selectedTaskDef()} keyed>
                                    {(def) => (
                                        <Show when={def.task.parameters.length > 0}>
                                            <div class={styles.argsBlock}>
                                                <For each={def.task.parameters}>{(param) => {
                                                    const key = `${def.plugin}:${def.task.name}`;
                                                    const value = () => taskArgs()[key]?.[param.name] ?? '';
                                                    return (
                                                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                                            <span class={styles.fieldLabel} title={param.description}>
                                                                {param.name}{param.required ? ' *' : ''}
                                                            </span>
                                                            <TaskParameterInput
                                                                botId={targetBotIds()[0]}
                                                                parameter={param}
                                                                sharedAcrossBots={targetBotIds().length > 1}
                                                                value={value()}
                                                                onChange={(next) => setArg(key, param.name, next)} />
                                                        </label>
                                                    );
                                                }}</For>
                                            </div>
                                        </Show>
                                    )}
                                </Show>
                            </Show>

                            <div class="h" style={{ 'justify-content': 'flex-end' }}>
                                <button type="submit" class="btn btn-primary"
                                    disabled={!canEnqueueTask()}>
                                    <Show when={dispatching()} fallback={<><i class="fa-solid fa-plus" /> <Text val="MULTI_ENQUEUE" /></>}>
                                        <i class="fa-solid fa-spinner fa-spin" /> <Text val="MULTI_SENDING" />
                                    </Show>
                                </button>
                            </div>
                        </form>
                    </Show>
                </section>
            </div>
        </div>
    );
}
